import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../App';
import { useOutbox } from './OfflineBar';
import { 
  ClipboardDocumentListIcon, 
  TruckIcon, 
  WrenchScrewdriverIcon,
  ShieldCheckIcon,
  ExclamationTriangleIcon,
  CalendarIcon,
  UserIcon,
  CheckCircleIcon,
  XCircleIcon,
  CameraIcon,
  PlusIcon,
  UserGroupIcon,
  LockClosedIcon,
  ArchiveBoxIcon,
  ArrowDownTrayIcon,
  EnvelopeIcon,
  TrashIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';

const EMAIL_REPORT_ROLES = [
  'super_admin', 'admin', 'office_admin', 'project_manager',
  'project_manager_estimator', 'foreman', 'shop_foreman',
  'millwright_foreman', 'fabrication_manager'
];

const MobileInspections = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [inspections, setInspections] = useState([]);
  const [openPermits, setOpenPermits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [busyId, setBusyId] = useState(null);
  const [emailModal, setEmailModal] = useState(null); // { inspection, to, subject, message }
  const outbox = useOutbox();
  const queuedPsis = outbox.items.filter((i) => i.type === 'psi_submit' && (i.payload?.inspection?.inspection_date || '').slice(0, 10) === selectedDate);
  const [sending, setSending] = useState(false);

  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const canEmail = user && EMAIL_REPORT_ROLES.includes(user.role);
  const canDelete = user && user.role === 'super_admin';

  const deleteInspection = async (inspection) => {
    const label = inspection?.inspection_type?.replace(/_/g, ' ') || 'inspection';
    if (!window.confirm(`Delete this ${label} by ${inspection?.inspector_name || 'unknown'}?\n\nThis permanently removes it — use only for tests or mistakes.`)) return;
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${backendUrl}/api/inspections/${inspection.inspection_id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        setInspections(prev => prev.filter(i => i.inspection_id !== inspection.inspection_id));
        setOpenPermits(prev => prev.filter(p => p.inspection_id !== inspection.inspection_id));
      } else {
        alert(data.detail || 'Delete failed');
      }
    } catch (e) {
      alert('Delete failed');
    }
  };

  const downloadReport = async (inspection) => {
    const id = inspection?.inspection_id;
    if (!id) return;
    setBusyId(id);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${backendUrl}/api/inspections/${id}/download-pdf`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) { alert('Could not generate the PDF for this report.'); return; }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank'); // opens the letterhead PDF for print/save
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      console.error('Download failed', e);
      alert('Could not download the PDF.');
    } finally {
      setBusyId(null);
    }
  };

  const openEmailModal = (inspection) => {
    const typeLabel = (inspection?.inspection_type || 'report').replace(/_/g, ' ')
      .replace(/\b\w/g, c => c.toUpperCase());
    setEmailModal({
      inspection,
      to: '',
      subject: `${typeLabel} — 365 Industrial Solutions — ${inspection?.inspection_date || ''}`,
      message: `Please find attached the ${typeLabel} from 365 Industrial Solutions.`
    });
  };

  const sendEmail = async () => {
    if (!emailModal?.to?.trim()) { alert('Please enter a recipient email.'); return; }
    setSending(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${backendUrl}/api/inspections/${emailModal.inspection.inspection_id}/email-pdf`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ to: emailModal.to, subject: emailModal.subject, message: emailModal.message })
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        alert(`Report emailed to ${emailModal.to} (a copy was sent to you).`);
        setEmailModal(null);
      } else {
        alert(data.detail || 'Could not send the email.');
      }
    } catch (e) {
      console.error('Email failed', e);
      alert('Could not send the email.');
    } finally {
      setSending(false);
    }
  };

  const inspectionTypes = [
    {
      id: 'daily_psi',
      name: 'Daily PSI',
      description: 'Pre-Job Safety Instructions',
      icon: ShieldCheckIcon,
      color: 'bg-blue-500',
      frequency: 'Daily'
    },
    {
      id: 'truck_inspection',
      name: 'Truck Inspection',
      description: 'CVOR Schedule 1 Daily Inspection',
      icon: TruckIcon,
      color: 'bg-green-500',
      frequency: 'Pre-use'
    },
    {
      id: 'trailer_inspection',
      name: 'Trailer Inspection',
      description: 'Trailer Safety / Schedule 1 Check',
      icon: ArchiveBoxIcon,
      color: 'bg-teal-500',
      frequency: 'Pre-use'
    },
    {
      id: 'forklift_inspection',
      name: 'Forklift Inspection',
      description: 'Forklift Safety Check',
      icon: WrenchScrewdriverIcon,
      color: 'bg-yellow-500',
      frequency: 'Pre-use'
    },
    {
      id: 'harness_lanyard_inspection',
      name: 'Harness & Lanyard',
      description: 'Fall Protection Check',
      icon: ShieldCheckIcon,
      color: 'bg-red-500',
      frequency: 'Pre-use'
    },
    {
      id: 'aerial_lift_inspection',
      name: 'Aerial Lift',
      description: 'Aerial Lift Pre-Op Inspection',
      icon: WrenchScrewdriverIcon,
      color: 'bg-purple-500',
      frequency: 'Pre-use'
    },
    {
      id: 'overhead_crane_inspection',
      name: 'Overhead Crane',
      description: 'Crane Safety Inspection',
      icon: WrenchScrewdriverIcon,
      color: 'bg-indigo-500',
      frequency: 'Daily'
    },
    {
      id: 'accident_incident_report',
      name: 'Accident/Incident',
      description: 'Incident Report Form',
      icon: ExclamationTriangleIcon,
      color: 'bg-red-600',
      frequency: 'As Needed'
    },
    {
      id: 'welder_inspection',
      name: 'Welding Machine',
      description: 'Daily Welder Inspection',
      icon: WrenchScrewdriverIcon,
      color: 'bg-red-500',
      frequency: 'Daily'
    },
    {
      id: 'hot_work_permit',
      name: 'Hot Work Permit',
      description: 'Welding/Cutting Permit',
      icon: ShieldCheckIcon,
      color: 'bg-orange-500',
      frequency: 'Per Job'
    },
    {
      id: 'confined_space_permit',
      name: 'Confined Space Entry',
      description: 'Confined Space Entry Permit',
      icon: ExclamationTriangleIcon,
      color: 'bg-amber-600',
      frequency: 'Per Entry'
    },
    {
      id: 'lockout_tagout',
      name: 'Lockout / Tagout',
      description: 'Energy Isolation (LOTO)',
      icon: LockClosedIcon,
      color: 'bg-slate-200',
      frequency: 'Per Job'
    }
  ];

  useEffect(() => {
    console.log('MobileInspections: Loading inspections for date:', selectedDate);
    console.log('MobileInspections: Backend URL:', backendUrl);
    loadInspections();
    loadOpenPermits();
  }, [selectedDate, location.state?.refresh]);

  const loadOpenPermits = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${backendUrl}/api/inspections/permits/open`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setOpenPermits(Array.isArray(data.permits) ? data.permits : []);
      }
    } catch (e) {
      console.error('Failed to load open permits:', e);
    }
  };

  const loadInspections = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token || !backendUrl) {
        console.log('Missing token or backend URL');
        setInspections([]);
        setLoading(false);
        return;
      }

      const response = await fetch(`${backendUrl}/api/inspections/by-date/${selectedDate}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setInspections(Array.isArray(data.inspections) ? data.inspections : []);
      } else {
        console.log('API response not OK:', response.status);
        setInspections([]);
      }
    } catch (error) {
      console.error('Failed to load inspections:', error);
      setInspections([]);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pass': return 'text-green-500';
      case 'fail': return 'text-red-500';
      case 'needs_attention': return 'text-amber-600';
      default: return 'text-gray-500';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pass': return CheckCircleIcon;
      case 'fail': return XCircleIcon;
      case 'needs_attention': return ExclamationTriangleIcon;
      default: return ClipboardDocumentListIcon;
    }
  };

  const startInspection = (inspectionType) => {
    navigate(`/mobile/inspection/${inspectionType}`);
  };

  const viewInspection = (inspection) => {
    if (inspection && inspection.inspection_type && inspection.inspection_id) {
      navigate(`/mobile/inspection/${inspection.inspection_type}/${inspection.inspection_id}`);
    }
  };

  const formatTime = (timeString) => {
    try {
      return timeString && typeof timeString === 'string' 
        ? new Date(`2000-01-01T${timeString}`).toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
          }) 
        : '';
    } catch (error) {
      return '';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-cyan-50">
      {/* Header with Back Button */}
      <div className="bg-blue-600 text-white p-4 shadow-lg">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/dashboard')}
              className="p-2 hover:bg-blue-700 rounded-lg transition-colors"
              title="Back to Dashboard"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
            </button>
            <div>
              <h1 className="text-xl font-bold">Safety Inspections</h1>
              <p className="text-blue-800 text-sm">Mobile inspection system</p>
            </div>
          </div>
        </div>
      </div>

      {/* Date selector */}
      <div className="bg-white/60 backdrop-blur-xl border-b border-slate-200 p-4 mx-4 mt-4 rounded-lg">
        <label className="block text-sm font-medium text-slate-800 mb-2">
          Inspection Date
        </label>
        <input
          type="date"
          value={selectedDate}
          onChange={(e) => setSelectedDate(e.target.value)}
          className="w-full p-3 bg-white/80 border border-slate-200 rounded-lg text-lg text-slate-900"
        />
      </div>

      {/* Open Confined Space Permits */}
      {openPermits.length > 0 && (
        <div className="bg-amber-50 border-2 border-amber-400 p-4 mx-4 mt-4 rounded-lg" data-testid="open-permits-section">
          <h2 className="text-lg font-semibold text-amber-900 mb-1 flex items-center">
            <ExclamationTriangleIcon className="h-6 w-6 mr-2 text-amber-600" />
            Open Confined Space Permits
          </h2>
          <p className="text-xs text-amber-700 mb-3">These permits are still OPEN — add atmospheric tests through the day, then close out with signatures when access is complete.</p>
          <div className="space-y-2">
            {openPermits.map((p) => (
              <button
                key={p.inspection_id}
                onClick={() => navigate(`/mobile/permit/${p.inspection_id}`)}
                data-testid={`open-permit-${p.inspection_id}`}
                className="w-full flex items-center justify-between p-3 bg-white rounded-lg border border-amber-300 shadow-sm hover:bg-amber-100 text-left"
              >
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-slate-900 text-sm truncate">
                    {p.space_description || 'Confined space'}{p.work_order_number ? ` — WO ${p.work_order_number}` : ''}
                  </p>
                  <p className="text-xs text-slate-600 truncate">
                    {p.job_site ? `${p.job_site} • ` : ''}Opened {formatTime(p.opened_time)} by {p.inspector_name}
                  </p>
                </div>
                <div className="text-right ml-2 flex-shrink-0">
                  <span className="inline-block px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[11px] font-bold">OPEN</span>
                  <p className="text-[11px] text-slate-500 mt-0.5">{p.test_count} test{p.test_count === 1 ? '' : 's'}</p>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div className="bg-white/60 backdrop-blur-xl border border-slate-200 p-4 mx-4 mt-4 mb-4 rounded-lg">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-slate-900 flex items-center">
            <PlusIcon className="h-6 w-6 mr-2 text-blue-600" />
            Start New Inspection
          </h2>
          <button
            onClick={() => navigate('/mobile/field-crew')}
            className="flex items-center px-3 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700"
            data-testid="field-crew-btn"
          >
            <UserGroupIcon className="h-4 w-4 mr-1.5" />
            Field Crew
          </button>
        </div>
        <div className="grid grid-cols-1 gap-3">
          {inspectionTypes.map((type) => {
            const Icon = type.icon;
            return (
              <button
                key={type.id}
                onClick={() => startInspection(type.id)}
                className="flex items-center p-4 bg-white/70 backdrop-blur-sm rounded-lg border border-slate-200 hover:bg-white/90 transition-colors shadow-md"
              >
                <div className={`p-3 rounded-lg ${type.color} mr-4 shadow-lg`}>
                  <Icon className="h-6 w-6 text-slate-900" />
                </div>
                <div className="flex-1 text-left">
                  <h3 className="font-semibold text-slate-900">{type.name}</h3>
                  <p className="text-sm text-slate-700">{type.description}</p>
                  <p className="text-xs text-slate-600">{type.frequency}</p>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Today's Inspections */}
      <div className="bg-white/60 backdrop-blur-xl border border-slate-200 p-4 mx-4 mb-4 rounded-lg">
        <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center">
          <CalendarIcon className="h-6 w-6 mr-2 text-slate-700" />
          Inspections for {new Date(selectedDate).toLocaleDateString()}
        </h2>

        {queuedPsis.length > 0 && (
          <div className="mb-3 space-y-2" data-testid="queued-psis">
            {queuedPsis.map((q) => (
              <div key={q.id} className={`flex items-center p-4 rounded-lg border shadow-md ${q.status === 'failed' ? 'bg-red-50 border-red-300' : 'bg-amber-50 border-amber-300'}`} data-testid={`queued-psi-${q.id}`}>
                <ExclamationTriangleIcon className={`h-6 w-6 mr-3 flex-shrink-0 ${q.status === 'failed' ? 'text-red-600' : 'text-amber-600'}`} />
                <div className="min-w-0">
                  <p className="font-semibold text-slate-900 text-sm">Daily PSI — {q.status === 'failed' ? "didn't go through" : 'saved on this phone'}</p>
                  <p className="text-xs text-slate-700 truncate">{q.label}</p>
                  <p className="text-xs text-slate-600">{q.status === 'failed' ? `Rejected: ${q.error} — open the Outbox bar below to retry` : 'Uploads on its own when the phone is back in service.'}</p>
                </div>
              </div>
            ))}
          </div>
        )}
        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="text-slate-600 mt-2">Loading inspections...</p>
          </div>
        ) : inspections.length === 0 ? (
          <div className="text-center py-8">
            <ClipboardDocumentListIcon className="h-12 w-12 text-slate-500 mx-auto mb-4" />
            <p className="text-slate-600">No inspections found for this date</p>
            <p className="text-sm text-slate-500">Start a new inspection above</p>
          </div>
        ) : (
          <div className="space-y-3">
            {inspections.map((inspection) => {
              try {
                const StatusIcon = getStatusIcon(inspection?.overall_status);
                const inspectionType = inspectionTypes.find(t => t.id === inspection?.inspection_type);
                
                return (
                  <div
                    key={inspection?.inspection_id || Math.random()}
                    onClick={() => viewInspection(inspection)}
                    className="flex items-center p-4 bg-white/70 backdrop-blur-sm border border-slate-200 rounded-lg hover:bg-white/90 cursor-pointer transition-colors shadow-md"
                  >
                    <div className={`p-2 rounded-lg ${inspectionType?.color || 'bg-gray-500'} mr-4`}>
                      <StatusIcon className={`h-6 w-6 text-slate-900`} />
                    </div>
                    
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-800">
                        {inspectionType?.name || inspection?.inspection_type || 'Unknown Inspection'}
                      </h3>
                      <p className="text-sm text-gray-600">
                        {inspection?.equipment_id && `Equipment: ${inspection.equipment_id}`}
                        {inspection?.job_site && ` • Site: ${inspection.job_site}`}
                      </p>
                      <div className="flex items-center text-xs text-gray-500 mt-1">
                        <UserIcon className="h-4 w-4 mr-1" />
                        {inspection?.inspector_name || 'Unknown Inspector'}
                        <span className="mx-2">•</span>
                        {formatTime(inspection?.inspection_time)}
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <div className="text-right">
                        <StatusIcon className={`h-6 w-6 ${getStatusColor(inspection?.overall_status)}`} />
                        <p className={`text-xs font-medium ${getStatusColor(inspection?.overall_status)}`}>
                          {(inspection?.overall_status || 'unknown').replace('_', ' ').toUpperCase()}
                        </p>
                      </div>
                      <div className="flex flex-col gap-1.5" onClick={(e) => e.stopPropagation()}>
                        <button
                          onClick={() => downloadReport(inspection)}
                          disabled={busyId === inspection?.inspection_id}
                          title="Download / Print PDF"
                          data-testid={`report-download-${inspection?.inspection_id}`}
                          className="p-2 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-lg border border-blue-200 disabled:opacity-50"
                        >
                          <ArrowDownTrayIcon className="h-4 w-4" />
                        </button>
                        {canEmail && (
                          <button
                            onClick={() => openEmailModal(inspection)}
                            title="Email PDF"
                            data-testid={`report-email-${inspection?.inspection_id}`}
                            className="p-2 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 rounded-lg border border-emerald-200"
                          >
                            <EnvelopeIcon className="h-4 w-4" />
                          </button>
                        )}
                        {canDelete && (
                          <button
                            onClick={() => deleteInspection(inspection)}
                            title="Delete inspection (super admin)"
                            data-testid={`report-delete-${inspection?.inspection_id}`}
                            className="p-2 bg-red-50 hover:bg-red-100 text-red-600 rounded-lg border border-red-200"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              } catch (error) {
                console.error('Error rendering inspection:', error, inspection);
                return null;
              }
            })}
          </div>
        )}
      </div>

      {/* Email report modal */}
      {emailModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => !sending && setEmailModal(null)}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="report-email-modal">
            <div className="flex items-center justify-between p-4 border-b border-slate-200">
              <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                <EnvelopeIcon className="h-5 w-5 text-emerald-600" /> Email Report
              </h2>
              <button onClick={() => !sending && setEmailModal(null)}><XMarkIcon className="h-5 w-5 text-slate-500" /></button>
            </div>
            <div className="p-4 space-y-3">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Recipient email <span className="text-red-500">*</span></label>
                <input
                  type="email"
                  value={emailModal.to}
                  onChange={(e) => setEmailModal({ ...emailModal, to: e.target.value })}
                  placeholder="office@subcontractor.com"
                  data-testid="report-email-to"
                  className="w-full p-2.5 border border-slate-300 rounded-lg text-slate-900"
                />
                <p className="text-xs text-slate-500 mt-1">A copy will also be sent to you.</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Subject</label>
                <input
                  type="text"
                  value={emailModal.subject}
                  onChange={(e) => setEmailModal({ ...emailModal, subject: e.target.value })}
                  data-testid="report-email-subject"
                  className="w-full p-2.5 border border-slate-300 rounded-lg text-slate-900"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Message</label>
                <textarea
                  rows={3}
                  value={emailModal.message}
                  onChange={(e) => setEmailModal({ ...emailModal, message: e.target.value })}
                  data-testid="report-email-message"
                  className="w-full p-2.5 border border-slate-300 rounded-lg text-slate-900"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 p-4 border-t border-slate-200">
              <button onClick={() => !sending && setEmailModal(null)} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg">Cancel</button>
              <button onClick={sendEmail} disabled={sending} data-testid="report-email-send" className="px-5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-medium disabled:opacity-50">
                {sending ? 'Sending…' : 'Send Email'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bottom padding for mobile */}
      <div className="h-20"></div>
    </div>
  );
};

export default MobileInspections;